import * as ChannelService from "@channel.io/channel-web-sdk-loader";

/**
 * ChannelIO가 실제로 로드된 경우에만 콜백과 세션을 정리한다.
 *
 * SDK loader의 종료 API는 loadScript() 전에 호출하면 콘솔 오류를 남기므로,
 * 아직 ChannelTalk을 로드하지 않았으면 아무 작업도 하지 않는다.
 */
export function shutdownChannelTalk() {
  if (
    typeof window === "undefined" ||
    !(window as Window & { ChannelIO?: unknown }).ChannelIO
  ) {
    return;
  }

  ChannelService.clearCallbacks();
  ChannelService.shutdown();
}

// 이 브라우저에서 멤버로 boot 한 적이 있는지. 탭끼리 공유해야 해서 localStorage 에 둔다.
const MEMBER_SESSION_KEY = "pathrade.channelTalk.memberSession";

// 채널톡 SDK 가 신원을 남기는 곳 (ch-plugin-core 코드로 확인, 문서화되지 않은 내부 구현).
// boot 요청에 sessionJWT(ch-session-{channelId} 쿠키, 30분)와 veilId(ch-veil-id 쿠키 + localStorage)를
// 그대로 싣는데, shutdown() 은 메모리 토큰만 비우고 이것들은 지우지 않는다.
// 그래서 멤버 세션 뒤에 memberId 없이 boot 하면 익명이 아니라 직전 멤버로 붙는다.
// SDK 가 바뀌어 익명 boot 에 다시 멤버가 붙으면 이 이름들부터 확인할 것.
const SESSION_COOKIE_PREFIX = "ch-session-";
const VEIL_ID_COOKIE = "ch-veil-id";
const VEIL_ID_STORAGE_KEY = "Channel.ch-veil-id";

/** 멤버로 boot 하기 직전에 호출한다. 이후 익명 boot 에서 채널톡 신원을 지우는 근거가 된다. */
export function rememberChannelTalkMemberSession() {
  try {
    window.localStorage.setItem(MEMBER_SESSION_KEY, "true");
  } catch {
    // localStorage 가 막혀 있으면 표시를 못 남길 뿐, boot 자체는 진행한다.
  }
}

/**
 * 멤버로 boot 한 적이 있을 때만 채널톡이 남긴 신원(세션 쿠키·veil)을 지운다. 익명 boot 직전에 호출한다.
 *
 * 매번 지우지 않는 이유: 로그인한 적 없는 방문자는 veil 로 이어져야 새로고침해도 상담이 이어진다.
 */
export function clearChannelTalkMemberSession() {
  try {
    if (!window.localStorage.getItem(MEMBER_SESSION_KEY)) return;
    window.localStorage.removeItem(MEMBER_SESSION_KEY);
    window.localStorage.removeItem(VEIL_ID_STORAGE_KEY);
  } catch {
    return;
  }

  const cookieNames = document.cookie
    .split(";")
    .map((cookie) => cookie.split("=")[0].trim())
    .filter(
      (name) =>
        name.startsWith(SESSION_COOKIE_PREFIX) || name === VEIL_ID_COOKIE
    );
  cookieNames.forEach(expireCookie);
}

// 채널톡은 boot 응답의 cookieDomain 으로 Domain 을 붙여 쿠키를 심는다.
// 어떤 Domain 이었는지 알 수 없으니 Domain 없이, 그리고 현재 호스트와 상위 도메인마다 한 번씩 만료시킨다.
function expireCookie(name: string) {
  const labels = window.location.hostname.split(".");
  const domains = labels.map((_, index) => labels.slice(index).join("."));

  document.cookie = `${name}=;Max-Age=0;Path=/`;
  domains.forEach((domain) => {
    document.cookie = `${name}=;Max-Age=0;Path=/;Domain=${domain}`;
  });
}
